"use client";

import Link from "next/link";
import type { Project, ToolRunModality } from "@/lib/types";
import { useLocale } from "@/lib/context/LocaleProvider";
import { ArrowRight, ChevronRight, History } from "@/components/ui/icons";

function modalityLabel(modality: ToolRunModality): string {
  return modality.charAt(0).toUpperCase() + modality.slice(1);
}

/** Project summary card for the Projects grid. */
export function ProjectCard({ project }: { project: Project }) {
  const { t } = useLocale();
  const empty = project.items.length === 0;
  const itemCount = project.generationCount ?? project.items.length;

  return (
    <div className="rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface p-[18px]">
      <Link
        href={`/projects/${project.slug}`}
        className="-mx-1.5 -mt-1.5 mb-1 flex cursor-pointer flex-col gap-0.5 rounded-lg p-1.5 transition-colors hover:bg-[rgba(17,17,17,0.03)]"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 truncate text-[16.5px] font-semibold tracking-[-0.2px] text-ink">
            {project.name}
          </div>
          <div className="flex flex-none items-center gap-1.5">
            <span className="whitespace-nowrap text-xs text-muted">
              {itemCount} items
            </span>
            <ChevronRight size={15} className="text-muted" />
          </div>
        </div>
        {project.desc ? (
          <div className="text-[11.5px] leading-[1.45] text-muted">
            {project.desc}
          </div>
        ) : null}
      </Link>

      {empty ? (
        <div className="mt-3 flex flex-col items-center justify-center gap-1.5 px-2 py-5 text-center">
          <History size={15} className="text-muted opacity-70" />
          <div className="text-[12.5px] font-semibold tracking-[-0.15px] text-ink">
            {t.cpEmptyProjTitle}
          </div>
          <p className="text-[12px] leading-[1.45] text-muted [text-wrap:pretty]">
            {t.cpEmptyProj}
          </p>
          <Link
            href="/tools"
            className="mt-2 inline-flex h-[34px] items-center justify-center gap-1.5 rounded-full bg-accent px-4 text-[12.5px] font-semibold text-white transition hover:brightness-105"
          >
            <span>{t.emptyHistoryCta}</span>
            <ArrowRight size={13} />
          </Link>
        </div>
      ) : (
        <div className="mt-2.5 flex flex-col">
          {project.items.map((item) => (
            <Link
              key={item.slug}
              href={`/history/${item.slug}`}
              className="flex cursor-pointer items-center gap-2.5 border-t border-[rgba(26,24,22,0.07)] py-2.5 transition-opacity hover:opacity-70"
            >
              <span className="h-2 w-2 flex-none rounded-full bg-accent" />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <div className="truncate text-[13.5px] font-semibold text-ink">
                    {item.name}
                  </div>
                  {item.modality ? (
                    <span className="inline-flex flex-none items-center rounded-full bg-[rgba(26,24,22,0.05)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.6px] text-muted">
                      {modalityLabel(item.modality)}
                    </span>
                  ) : null}
                </div>
                <div className="text-[11.5px] text-muted">{item.tool}</div>
              </div>
              <div className="whitespace-nowrap text-[11.5px] text-muted">
                {item.generated ? t.tfToday : item.date}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
